import { isEmpty } from 'lodash';
import { FetchResult } from 'src/types/TApiQueries';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

export async function getUsers(searchFilters: any, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users`;

  const offset = (searchFilters.page - 1) * 30;

  const filters: any = {
    offset,
  };

  Object.entries(searchFilters).forEach(([key, value]) => {
    if (!isEmpty(value)) {
      filters[key] = value;
    }
  });

  // `isEmpty` is true for every boolean, so flags never survive the loop
  // above. Pass this one explicitly (and only when set) — sending the whole
  // set of booleans would start applying `enabled: false` as "show only
  // disabled users", which is not what the unchecked filter means.
  if (searchFilters.include_removed) {
    filters.include_removed = true;
  }

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(filters),
  });
}

export async function getUser(id: number, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${id}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function searchUsers(filters: any, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(filters),
  });
}

export async function addUser(user: any, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/new`;

  user.role_id = parseInt(user.role_id);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(user),
  });
}

export async function editUser(id: number, data: any, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${id}`;

  if (data.role_id) {
    data.role_id = parseInt(data.role_id);
  }

  return performFetchWithLogging(url, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function resendSignup(userId: number, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${userId}/resendsignup`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// --- Seat lifecycle (plan-structure v2 Phase 7a) ---------------------------
// Removing a member is NOT a delete: the account is kept so their assets keep
// their author, login is closed off and the SEAT is freed. `restoreMember` is
// the exact inverse and can 403 with `plan_upgrade_required` when no seat is
// free. `reassignInvite` re-points an UNACCEPTED invitation at someone else.

export async function removeMember(userId: number, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${userId}/remove`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

export async function restoreMember(userId: number, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${userId}/restore`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

export async function reassignInvite(
  userId: number,
  email: string,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/users/${userId}/reassign`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
}
